import { forwardRef, type ButtonHTMLAttributes, type ReactElement } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from './icon';

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center font-semibold transition-all whitespace-nowrap',
    'rounded-[var(--r-sm)] select-none',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ink)] focus-visible:ring-offset-2',
    'focus-visible:ring-offset-[var(--bg-surface)]',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'active:scale-[0.98]',
  ),
  {
    variants: {
      variant: {
        primary: 'primary bg-[var(--ink)] text-[var(--bg-surface)] hover:bg-[var(--brand-ink)]',
        secondary:
          'secondary bg-[var(--bg-surface)] border border-[var(--rule-strong)] text-[var(--ink)] hover:bg-[var(--bg-soft)]',
        soft: 'soft bg-[var(--bg-soft)] text-[var(--ink)] hover:bg-[var(--bg-inset)]',
        ghost: 'ghost text-[var(--ink-2)] hover:text-[var(--ink)] hover:bg-[var(--bg-soft)]',
        danger: 'danger bg-[var(--err-wash)] text-[var(--err)] hover:bg-[var(--err)] hover:text-white',
        brand: 'brand bg-[var(--brand)] text-white hover:bg-[var(--brand-ink)]',
      },
      size: {
        sm: 'h-8 px-3 text-[12px] gap-1.5',
        md: 'h-10 px-4 text-[13px] gap-2',
        lg: 'h-12 px-5 text-[14px] gap-2',
      },
      fullWidth: { true: 'w-full' },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    icon?: IconName;
    iconRight?: IconName;
  };

const ICON_SIZES: Readonly<Record<'sm' | 'md' | 'lg', number>> = { sm: 13, md: 15, lg: 17 };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, fullWidth, icon, iconRight, type = 'button', children, ...rest },
  ref,
): ReactElement {
  const resolvedSize = size ?? 'md';
  const iconSize = ICON_SIZES[resolvedSize];
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...rest}
    >
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconRight && <Icon name={iconRight} size={iconSize} />}
    </button>
  );
});

export { buttonVariants };
